import React, { useEffect, useRef, useState } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ContentSlider } from '../../common/ui';

const styles = cssMap({
	floatingPositioner: {
		position: 'fixed',
		bottom: token('space.100'),
		zIndex: 400,
		pointerEvents: 'none',
		display: 'flex',
		justifyContent: 'center',
		transition: 'width 300ms cubic-bezier(0.2, 0, 0, 1)',
	},
	bottomAlignment: {
		bottom: token('space.300'),
	},
	parentDetector: {
		position: 'absolute',
		visibility: 'hidden',
		pointerEvents: 'none',
	},
});

const MARGIN_BUFFER = 200; // Constant for margin from container edges

export const usePrevious = <T,>(value: T): T | undefined => {
	const ref = useRef<T | undefined>();
	useEffect(() => {
		ref.current = value;
	}, [value]);
	return ref.current;
};

export const ToolbarWrapper = ({
	children,
	show,
	minToolbarWidth,
	width,
	onDeselectAll,
}: {
	children: (maxToolbarWidth?: number) => React.ReactNode;
	minToolbarWidth?: number;
	onDeselectAll?: () => void;
	show: boolean;
	width?: string;
}): React.JSX.Element => {
	const previousShow = usePrevious(show);
	const parentDetectorRef = useRef<HTMLDivElement>(null);
	const [maxToolbarWidth, setMaxToolbarWidth] = useState(minToolbarWidth || 0);
	const [toolbarLeft, setToolbarLeft] = useState(0);

	useEffect(() => {
		if (previousShow && !show) {
			onDeselectAll?.();
		}
	}, [onDeselectAll, previousShow, show]);

	if (fg('responsive_bulk_ops_toolbar_platform')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			const detector = parentDetectorRef.current;
			if (!detector) {
				return;
			}
			const parentContainer = detector.parentElement;
			if (!parentContainer) {
				return;
			}
			const updateMaxWidth = () => {
				const containerRect = parentContainer.getBoundingClientRect();
				const containerWidth = containerRect.width;
				const containerLeft = containerRect.left;
				const calculatedMaxWidth = Math.max(minToolbarWidth ?? 0, containerWidth - MARGIN_BUFFER);
				setMaxToolbarWidth(calculatedMaxWidth);
				setToolbarLeft(containerLeft + MARGIN_BUFFER / 2);
			};

			const resizeObserver = new ResizeObserver(updateMaxWidth);
			resizeObserver.observe(parentContainer);

			// Initial calculation
			updateMaxWidth();

			return () => {
				resizeObserver.disconnect();
			};
		}, [minToolbarWidth, show]);
	}

	return (
		<>
			{/* Parent detector is used to calculate the width of the parent as position fixed elements are relative to the viewport. */}
			{fg('responsive_bulk_ops_toolbar_platform') && (
				<Box ref={parentDetectorRef} xcss={styles.parentDetector} />
			)}
			<Box
				xcss={cx(
					styles.floatingPositioner,
					fg('responsive_bulk_ops_toolbar_platform') && styles.bottomAlignment,
				)}
				style={{
					width: fg('responsive_bulk_ops_toolbar_platform')
						? maxToolbarWidth || width || '100%'
						: width || '100%',
					left: fg('responsive_bulk_ops_toolbar_platform') ? toolbarLeft : undefined,
				}}
			>
				<ContentSlider show={show}>
					{children(fg('responsive_bulk_ops_toolbar_platform') ? maxToolbarWidth : undefined)}
				</ContentSlider>
			</Box>
		</>
	);
};
